(() => {
  const productDetailsCache = new Map();

  const handleize = (value = '') =>
    value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[’']/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  // Exact SKU comparisons intentionally preserve punctuation and leading zeros.
  // Only whitespace and letter case are normalized.
  const normalizeExactSku = (value = '') => value.toString().trim().toLowerCase();

  const normalizeVendorAlias = (value = '') =>
    value
      .toString()
      .trim()
      .replace(/\b(?:oem|parts?|catalog)\b/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const getVendorAliases = (value = '') => {
    const pieces = value
      .toString()
      .split(/\s*(?:\/|\||&|\+|,|\band\b)\s*/i)
      .map(normalizeVendorAlias)
      .filter(Boolean);

    return [...new Set(pieces.map((piece) => piece.toLowerCase()))];
  };

  const vendorMatches = (productVendor = '', vendorScope = '') => {
    const aliases = getVendorAliases(vendorScope);
    if (!aliases.length) return true;

    const normalizedProductVendor = normalizeVendorAlias(productVendor).toLowerCase();
    if (!normalizedProductVendor) return false;

    return aliases.some(
      (alias) =>
        normalizedProductVendor === alias ||
        normalizedProductVendor.startsWith(`${alias} `) ||
        alias.startsWith(`${normalizedProductVendor} `)
    );
  };

  const getShopRoot = () =>
    window.Shopify?.routes?.root ||
    document.documentElement.getAttribute('data-shop-root') ||
    '/';

  const getProductHandle = (product = {}) => {
    if (product.handle) return handleize(product.handle);

    try {
      const url = new URL(product.url || '', window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      return match ? handleize(match[1]) : '';
    } catch (_) {
      const match = (product.url || '').match(/\/products\/([^/?#]+)/i);
      return match ? handleize(match[1]) : '';
    }
  };

  const getProductDetailsUrl = (product = {}) => {
    try {
      const url = new URL(product.url || '', window.location.origin);
      const productPath = url.pathname.match(/^(.*\/products\/[^/?#]+)/i)?.[1];
      return productPath ? `${productPath}.js` : '';
    } catch (_) {
      const productPath = (product.url || '').match(/^(.*\/products\/[^/?#]+)/i)?.[1];
      return productPath ? `${productPath}.js` : '';
    }
  };

  const fetchProductDetails = async (product, signal) => {
    const detailsUrl = getProductDetailsUrl(product);
    if (!detailsUrl) return null;

    if (productDetailsCache.has(detailsUrl)) {
      return productDetailsCache.get(detailsUrl);
    }

    const response = await fetch(detailsUrl, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });

    if (!response.ok) return null;

    const details = await response.json();
    productDetailsCache.set(detailsUrl, details);
    return details;
  };

  const fetchProductDetailsByHandle = async (handle, signal) => {
    const cleanHandle = handleize(handle);
    if (!cleanHandle) return null;

    const detailsUrl = `${getShopRoot()}products/${encodeURIComponent(cleanHandle)}.js`;

    if (productDetailsCache.has(detailsUrl)) {
      return productDetailsCache.get(detailsUrl);
    }

    const response = await fetch(detailsUrl, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });

    if (!response.ok) return null;

    const details = await response.json();
    productDetailsCache.set(detailsUrl, details);
    return details;
  };

  const getPartNumberFromProduct = (product, vendorScope) => {
    if (product.__rswwMatchedSku) return product.__rswwMatchedSku;
    if (product.__rswwDisplaySku) return product.__rswwDisplaySku;

    const productHandle = getProductHandle(product);
    const aliases = getVendorAliases(vendorScope);

    for (const alias of aliases) {
      const prefix = `${handleize(alias)}-`;
      if (prefix !== '-' && productHandle.startsWith(prefix)) {
        return productHandle.slice(prefix.length).toUpperCase();
      }
    }

    return '';
  };

  const findExactHandleMatch = (products, query, vendorScope) => {
    const queryHandle = handleize(query);
    if (!queryHandle) return null;

    const expectedHandles = getVendorAliases(vendorScope).map(
      (alias) => `${handleize(alias)}-${queryHandle}`
    );

    if (!expectedHandles.length) return null;

    return (
      products.find((product) => expectedHandles.includes(getProductHandle(product))) ||
      null
    );
  };

  const looksLikePartNumber = (value = '') => {
    const cleaned = value.trim();
    return cleaned.length >= 3 && !/\s/.test(cleaned) && /\d/.test(cleaned);
  };

  const findDirectExactSkuMatch = async (query, vendorScope, signal) => {
    const normalizedQuery = normalizeExactSku(query);
    const queryHandle = handleize(query);
    if (!normalizedQuery || !queryHandle) return null;

    const aliases = getVendorAliases(vendorScope);
    if (!aliases.length) return null;

    // RSWW's standardized OEM imports use vendor-SKU handles. Query those URLs
    // directly before predictive search ranking so an exact SKU cannot be
    // pushed out of Shopify's 10-result predictive response.
    for (const alias of aliases) {
      const expectedHandle = `${handleize(alias)}-${queryHandle}`;
      const details = await fetchProductDetailsByHandle(expectedHandle, signal);
      if (!details || !vendorMatches(details.vendor, vendorScope)) continue;

      const exactVariant = details.variants?.find(
        (variant) => normalizeExactSku(variant.sku) === normalizedQuery
      );

      if (!exactVariant) continue;

      return {
        title: details.title,
        vendor: details.vendor,
        url: `${getShopRoot()}products/${expectedHandle}`,
        handle: expectedHandle,
        featured_image: details.featured_image || '',
        image: details.featured_image || '',
        __rswwMatchedSku: exactVariant.sku,
        __rswwDisplaySku: exactVariant.sku,
      };
    }

    return null;
  };

  const hydrateDisplaySkus = async (products, query, vendorScope, signal) => {
    const normalizedQuery = normalizeExactSku(query);

    await Promise.all(
      products.map(async (product) => {
        if (product.__rswwMatchedSku || product.__rswwDisplaySku) return;

        // Newer standardized catalogs can derive their part number from the
        // vendor-SKU handle without another network request.
        const handleSku = getPartNumberFromProduct(product, vendorScope);
        if (handleSku) {
          product.__rswwDisplaySku = handleSku;
          return;
        }

        // Legacy catalogs (for example many Perkins/Bobcat/Doosan products)
        // do not encode the SKU in the handle. Fetch the product JSON so the
        // part number is still displayed for name/keyword searches.
        const details = await fetchProductDetails(product, signal);
        if (!details) return;

        const variantsWithSku = (details.variants || []).filter((variant) =>
          normalizeExactSku(variant.sku)
        );
        if (!variantsWithSku.length) return;

        const queryMatchedVariant = variantsWithSku.find((variant) =>
          normalizeExactSku(variant.sku).includes(normalizedQuery)
        );

        const displayVariant = queryMatchedVariant || variantsWithSku[0];
        product.__rswwDisplaySku = displayVariant.sku;
      })
    );

    return products;
  };

  const findExactSkuMatch = async (products, query, vendorScope, signal) => {
    const normalizedQuery = normalizeExactSku(query);
    if (!normalizedQuery || !products.length) return null;

    // First preserve the fast path for RSWW catalogs whose handle is vendor-SKU.
    const handleMatch = findExactHandleMatch(products, query, vendorScope);
    if (handleMatch) {
      const details = await fetchProductDetails(handleMatch, signal);
      const exactVariant = details?.variants?.find(
        (variant) => normalizeExactSku(variant.sku) === normalizedQuery
      );

      if (exactVariant && vendorMatches(details?.vendor || handleMatch.vendor, vendorScope)) {
        handleMatch.__rswwMatchedSku = exactVariant.sku;
        return handleMatch;
      }
    }

    // Older catalogs don't always use vendor-SKU handles. Validate the actual
    // Variant SKU returned by Shopify instead of guessing from the product URL.
    const checks = await Promise.all(
      products.map(async (product) => {
        const details = await fetchProductDetails(product, signal);
        if (!details || !vendorMatches(details.vendor || product.vendor, vendorScope)) {
          return null;
        }

        const exactVariant = details.variants?.find(
          (variant) => normalizeExactSku(variant.sku) === normalizedQuery
        );

        if (!exactVariant) return null;

        product.__rswwMatchedSku = exactVariant.sku;
        return product;
      })
    );

    return checks.find(Boolean) || null;
  };

  const initLargeCatalogSearch = (scope = document) => {
    scope.querySelectorAll('[data-large-catalog-search]').forEach((root) => {
      if (root.dataset.largeCatalogSearchInitialized === 'true') return;
      root.dataset.largeCatalogSearchInitialized = 'true';

      const input = root.querySelector('[data-large-catalog-search-input]');
      const results = root.querySelector('[data-large-catalog-search-results]');
      const clearButton = root.querySelector('[data-large-catalog-search-clear]');
      const spinner = root.querySelector('[data-large-catalog-search-spinner]');

      if (!input || !results) return;

      const vendor = (root.dataset.searchVendor || '').trim();
      const minimumCharacters = Math.max(
        1,
        Number.parseInt(root.dataset.searchMinimum || '2', 10) || 2
      );
      const showImages = root.dataset.searchShowImages !== 'false';

      let timer = null;
      let controller = null;
      let requestNumber = 0;
      let keyboardIndex = -1;

      const setExpanded = (expanded) => {
        input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        results.hidden = !expanded;
      };

      const setLoading = (loading) => {
        if (spinner) spinner.hidden = !loading;
        if (clearButton) clearButton.hidden = loading || input.value.length === 0;
      };

      const closeResults = () => {
        setExpanded(false);
        keyboardIndex = -1;
      };

      const clearResults = () => {
        results.replaceChildren();
        closeResults();
      };

      const addStatus = (message) => {
        const status = document.createElement('p');
        status.className = 'large-catalog-search__status';
        status.textContent = message;
        results.appendChild(status);
      };

      const buildProductResult = (product, isExactPartMatch = false) => {
        const link = document.createElement('a');
        link.className = 'large-catalog-search__result';
        link.href = product.url;
        link.setAttribute('role', 'option');
        link.setAttribute('tabindex', '-1');

        const imageWrap = document.createElement('span');
        imageWrap.className = 'large-catalog-search__result-image';

        const imageUrl =
          product.featured_image?.url ||
          product.image ||
          product.featured_image ||
          '';

        if (showImages && imageUrl) {
          const image = document.createElement('img');
          image.src = imageUrl;
          image.alt = product.featured_image?.alt || '';
          image.loading = 'lazy';
          imageWrap.appendChild(image);
        } else {
          imageWrap.classList.add('large-catalog-search__result-image--empty');
        }

        const copy = document.createElement('span');
        copy.className = 'large-catalog-search__result-copy';

        const titleRow = document.createElement('span');
        titleRow.className = 'large-catalog-search__result-title-row';

        const title = document.createElement('span');
        title.className = 'large-catalog-search__result-title';
        title.textContent = product.title || 'Product';
        titleRow.appendChild(title);

        if (isExactPartMatch) {
          const exactBadge = document.createElement('span');
          exactBadge.className = 'large-catalog-search__exact-badge';
          exactBadge.textContent = 'Exact match';
          titleRow.appendChild(exactBadge);
        }

        const meta = document.createElement('span');
        meta.className = 'large-catalog-search__result-meta';

        const vendorText = document.createElement('span');
        vendorText.className = 'large-catalog-search__result-vendor';
        vendorText.textContent = product.vendor || vendor;
        meta.appendChild(vendorText);

        const partNumber = getPartNumberFromProduct(product, vendor);
        if (partNumber) {
          const partText = document.createElement('span');
          partText.className = 'large-catalog-search__result-part';
          partText.textContent = `Part # ${partNumber}`;
          meta.appendChild(partText);
        }

        copy.append(titleRow, meta);
        link.append(imageWrap, copy);

        return link;
      };

      const renderResults = (products, query, isExactPartMatch = false) => {
        results.replaceChildren();
        keyboardIndex = -1;

        if (!products.length) {
          addStatus(`No ${vendor || 'catalog'} products found for “${query}”.`);
          setExpanded(true);
          return;
        }

        products.forEach((product) => {
          results.appendChild(buildProductResult(product, isExactPartMatch));
        });

        const footer = document.createElement('div');
        footer.className = 'large-catalog-search__footer';

        if (isExactPartMatch) {
          footer.textContent = `Exact part number match: ${query}`;
        } else {
          footer.textContent =
            products.length >= 10
              ? 'Showing the 10 closest matches. Add more of the part number or name to narrow the results.'
              : `${products.length} matching ${products.length === 1 ? 'product' : 'products'}`;
        }

        results.appendChild(footer);
        setExpanded(true);
      };

      const fetchPredictiveProducts = async ({ query, fields, signal }) => {
        const params = new URLSearchParams();
        params.set('q', query);
        params.set('resources[type]', 'product');
        params.set('resources[limit]', '10');
        params.set('resources[limit_scope]', 'each');
        params.set('resources[options][unavailable_products]', 'show');
        params.set('resources[options][fields]', fields);

        const response = await fetch(
          `${getShopRoot()}search/suggest.json?${params.toString()}`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
            signal,
          }
        );

        if (!response.ok) {
          throw new Error(`Predictive search returned ${response.status}`);
        }

        const payload = await response.json();
        return payload?.resources?.results?.products || [];
      };

      const filterToVendor = (products) => {
        if (!vendor) return products;
        return products.filter((product) => vendorMatches(product.vendor, vendor));
      };

      const mergeProducts = (groups) => {
        const merged = [];
        const seen = new Set();

        groups.flat().forEach((product) => {
          const key = getProductHandle(product) || product.url || product.id;
          if (!key || seen.has(key)) return;
          seen.add(key);
          merged.push(product);
        });

        return merged.slice(0, 10);
      };

      const fetchGeneralProducts = async (query, signal) => {
        const fields = 'title,body,product_type,tag,variants.title,variants.sku,vendor';
        const aliases = getVendorAliases(vendor);

        if (!aliases.length) {
          return fetchPredictiveProducts({ query, fields, signal });
        }

        // Scope descriptive searches to each OEM alias independently. This also
        // supports combined collections such as "Bobcat / Doosan".
        const scopedGroups = await Promise.all(
          aliases.slice(0, 4).map((alias) =>
            fetchPredictiveProducts({
              query: `${query} ${alias}`,
              fields,
              signal,
            })
          )
        );

        const scopedMatches = mergeProducts(scopedGroups.map(filterToVendor));
        if (scopedMatches.length) return scopedMatches;

        // Fallback for legacy vendor naming where collection title and Vendor are
        // not identical. Keep the client-side vendor check when possible.
        const rawMatches = await fetchPredictiveProducts({ query, fields, signal });
        const vendorMatchesOnly = filterToVendor(rawMatches);
        return vendorMatchesOnly.length ? vendorMatchesOnly : rawMatches;
      };

      const performSearch = async () => {
        const query = input.value.trim();
        clearButton && (clearButton.hidden = query.length === 0);

        if (query.length < minimumCharacters) {
          controller?.abort();
          clearResults();
          return;
        }

        controller?.abort();
        controller = new AbortController();
        const currentRequest = ++requestNumber;

        setLoading(true);

        try {
          // Stage 0: standardized RSWW catalogs use vendor-SKU handles. For
          // part-number-looking queries, try the exact product URL first. This
          // bypasses Shopify predictive-search ranking and makes exact SKU lookup
          // deterministic for Generac/Sullair and future standardized OEMs.
          if (looksLikePartNumber(query)) {
            const directExactPartMatch = await findDirectExactSkuMatch(
              query,
              vendor,
              controller.signal
            );

            if (currentRequest !== requestNumber) return;

            if (directExactPartMatch) {
              renderResults([directExactPartMatch], query, true);
              return;
            }
          }

          // Stage 1: search the customer's raw input against Variant SKU. For
          // legacy catalogs, verify the ACTUAL variant SKU from product JSON.
          const skuCandidates = await fetchPredictiveProducts({
            query,
            fields: 'variants.sku',
            signal: controller.signal,
          });

          if (currentRequest !== requestNumber) return;

          if (looksLikePartNumber(query)) {
            const exactPartMatch = await findExactSkuMatch(
              skuCandidates,
              query,
              vendor,
              controller.signal
            );

            if (currentRequest !== requestNumber) return;

            if (exactPartMatch) {
              renderResults([exactPartMatch], query, true);
              return;
            }
          }

          // Stage 2: no exact SKU match, so show partial SKU / product-name /
          // keyword matches, scoped to the OEM collection when possible. Hydrate
          // SKU metadata so every visible result shows its part number regardless
          // of whether the customer searched by SKU or by product name.
          const generalProducts = await fetchGeneralProducts(query, controller.signal);

          if (currentRequest !== requestNumber) return;

          await hydrateDisplaySkus(
            generalProducts,
            query,
            vendor,
            controller.signal
          );

          if (currentRequest !== requestNumber) return;

          renderResults(generalProducts, query, false);
        } catch (error) {
          if (error.name === 'AbortError') return;

          results.replaceChildren();
          addStatus('Search is temporarily unavailable. Please try again.');
          setExpanded(true);
          console.error('[RSWW large catalog search]', error);
        } finally {
          if (currentRequest === requestNumber) setLoading(false);
        }
      };

      const queueSearch = () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(performSearch, 220);
      };

      const getResultLinks = () =>
        Array.from(results.querySelectorAll('.large-catalog-search__result'));

      const updateKeyboardActive = (links) => {
        links.forEach((link, index) => {
          link.classList.toggle('is-keyboard-active', index === keyboardIndex);
        });

        if (keyboardIndex >= 0 && links[keyboardIndex]) {
          links[keyboardIndex].scrollIntoView({ block: 'nearest' });
        }
      };

      input.addEventListener('input', queueSearch);

      input.addEventListener('keydown', (event) => {
        const links = getResultLinks();

        if (event.key === 'Escape') {
          closeResults();
          return;
        }

        if (!links.length || results.hidden) return;

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          keyboardIndex = (keyboardIndex + 1) % links.length;
          updateKeyboardActive(links);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          keyboardIndex = keyboardIndex <= 0 ? links.length - 1 : keyboardIndex - 1;
          updateKeyboardActive(links);
        } else if (event.key === 'Enter' && keyboardIndex >= 0) {
          event.preventDefault();
          links[keyboardIndex].click();
        }
      });

      input.addEventListener('focus', () => {
        if (results.childElementCount > 0) setExpanded(true);
      });

      clearButton?.addEventListener('click', () => {
        controller?.abort();
        input.value = '';
        clearButton.hidden = true;
        clearResults();
        input.focus();
      });

      document.addEventListener('click', (event) => {
        if (!root.contains(event.target)) closeResults();
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initLargeCatalogSearch());
  } else {
    initLargeCatalogSearch();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initLargeCatalogSearch(event.target);
  });
})();
